import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Observable } from 'rxjs';
import { RestMaintenanceService } from 'src/app/service/rest/maintenance/rest-maintenance.service';
import { CheckOrder, Maintenance, MaintenanceCheck } from './maintenance.model';

@Injectable()
export class MaintenanceService {

  private _maintenance: Maintenance;
  private _serialNumber: string;
  private _serialNumberId: number;
  private _dataSourceChecks: MatTableDataSource<MaintenanceCheck> = new MatTableDataSource<MaintenanceCheck>();

  constructor(private maintenanceRest: RestMaintenanceService) { }

  public get maintenance(): Maintenance {
    return this._maintenance;
  }
  
  public set maintenance(value: Maintenance) {
    this._maintenance = value;
    if(value) this.dataSourceChecks = new MatTableDataSource(value.maintenanceChecks);
  }

  public get serialNumber(): string {
    return this._serialNumber;
  }
  
  public set serialNumber(value: string) {
    this._serialNumber = value;
  }

  public get serialNumberId(): number {
    return this._serialNumberId;
  }
  
  public set serialNumberId(value: number) {
    this._serialNumberId = value;
  }

  public get dataSourceChecks(): MatTableDataSource<MaintenanceCheck> {
    return this._dataSourceChecks;
  }
  
  public set dataSourceChecks(value: MatTableDataSource<MaintenanceCheck>) {
    this._dataSourceChecks = value;
  }

  get maintenanceChecks(): MaintenanceCheck[] {
    return this.maintenance.maintenanceChecks;
  }

  set maintenanceChecks(value : MaintenanceCheck[]){
    this.maintenance.maintenanceChecks = value;
  }

  /**
   * GET /Maintenance/{maintenanceId}
   * Retrieves a maintenance by ID
   */
  getMaintenanceById(maintenanceId: number, languageId: string, defaultLanguage: number = 0): Observable<Maintenance> {
    return this.maintenanceRest.getMaintenanceById(maintenanceId, languageId, defaultLanguage);
  }

  /**
   * GET /Maintenance/article/{articleId}
   * Retrieves maintenances by article ID
   */
  getMaintenancesByArticleId(articleId: number, languageId: string, defaultLanguage: number = 0): Observable<Maintenance[]> {
    return this.maintenanceRest.getMaintenancesByArticleId(articleId, languageId, defaultLanguage);
  }

  /**
   * GET /Maintenance/{articleId}/{languageId}
   * Retrieves maintenances by article ID and language ID (Admin only)
   */
  getMaintenancesByArticleIdAndLanguageId(articleId: number, languageId: string, defaultLanguage: number = 0): Observable<Maintenance[]> {
    return this.maintenanceRest.getMaintenancesByArticleIdAndLanguageId(articleId, languageId, defaultLanguage);
  }

  /**
   * POST /Maintenance
   * Creates a new maintenance (Admin only)
   */
  addMaintenance(): Observable<Maintenance> {
    return this.maintenanceRest.createMaintenance(this.maintenance);
  }

  /**
   * POST /Maintenance - with custom maintenance object
   * Creates a new maintenance (Admin only)
   */
  createMaintenance(maintenance: Maintenance): Observable<Maintenance> {
    return this.maintenanceRest.createMaintenance(maintenance);
  }

  /**
   * PUT /Maintenance/{maintenanceId}
   * Updates an existing maintenance (Admin only)
   */
  updateMaintenance(): Observable<Maintenance> {
    return this.maintenanceRest.updateMaintenance(this.maintenance.id, this.maintenance);
  }

  /**
   * PUT /Maintenance/{maintenanceId} - with custom maintenance object
   * Updates an existing maintenance (Admin only)
   */
  updateMaintenanceById(maintenanceId: number, maintenance: Maintenance): Observable<Maintenance> {
    return this.maintenanceRest.updateMaintenance(maintenanceId, maintenance);
  }

  /**
   * PUT /Maintenance/orderchecks
   * Updates the order of maintenance checks (Admin only)
   */
  updateMaintenanceChecksOrder(orderArray: CheckOrder[]): Observable<any> {
    return this.maintenanceRest.updateMaintenanceChecksOrder(orderArray);
  }

  /**
   * DELETE /Maintenance/{maintenanceId}
   * Deletes a maintenance (Admin only)
   */
  deleteMaintenance(): Observable<any> {
    return this.maintenanceRest.deleteMaintenance(this.maintenance.id);
  }

  /**
   * DELETE /Maintenance/{maintenanceId} - with custom ID
   * Deletes a maintenance (Admin only)
   */
  deleteMaintenanceById(maintenanceId: number): Observable<any> {
    return this.maintenanceRest.deleteMaintenance(maintenanceId);
  }

  /**
   * Legacy method - Clone maintenance functionality
   * @deprecated Use updateMaintenanceById instead
   */
  cloneMaintenance(clonedMaintenance: Maintenance): Observable<Maintenance> {
    return this.maintenanceRest.updateMaintenance(clonedMaintenance.id, clonedMaintenance);
  }

  /**
   * Clone maintenance content translations
   */
  cloneMaintenanceContents(request: any, languageId: string): Observable<any> {
    return this.maintenanceRest.cloneMaintenanceContents(request, languageId);
  }

  /**
   * Legacy method - Order checks
   * @deprecated Use updateMaintenanceChecksOrder instead
   */
  orderChecks(order: CheckOrder[]): void {
    this.maintenanceRest.orderChecks(order).subscribe(
      result => { }, 
      error => { console.log(error) }
    );
  }
}
